/**
 * web/src/hooks/mutations/useApproveGate.ts
 * Optimistic approve — mutates the cache immediately, fires success toast,
 * rolls back on error. WS event reconciles within ~ms (CONTEXT.md D-2.1/D-2.2).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { approveGate, type ApproveGateBody } from '../../api/gates';
import { gateOptimisticHandlers, type GateMutationVars } from './_optimistic';

/** Variables for the approve mutation. */
export type UseApproveGateVars = GateMutationVars;

/**
 * Approves a gate optimistically. Removes the gate from the pending list
 * immediately; on error, restores the cached list and surfaces a toast.
 * Optimistic + reconciliation handlers are shared with useSkipGate.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST; cache writes on the pending-gates list
 * @idempotent no — server returns 409 on a re-approval against a no-longer-waiting step
 */
export function useApproveGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: UseApproveGateVars) => {
      const body: ApproveGateBody = { edits: vars.edits };
      return approveGate(vars.workflowId, vars.stepIndex, body);
    },
    ...gateOptimisticHandlers(qc, {
      defaultSuccess: 'Approved — workflow resumed',
      errorFallback: 'Approve failed — please retry',
    }),
  });
}
